<template>
<section class="px-4 pt-4 pb-8 mx-auto max-w-5xl xl:px-0">
        <!-- Header Section -->
        <div class="mb-8 text-center">
            <h3 class="mb-4 text-2xl leading-none text-center lg:mb-8 sm:text-3xl md:text-32 font-ww-bold xl:px-12" v-html="mainHeading">
            </h3>
            <div class="mx-auto space-y-4">
                <div class="text-base text-left md:text-lg font-ww-regular" v-html="descriptionText1">
                </div>
            </div>
        </div>
        <!-- Main Content Section -->
        <div class="grid grid-cols-1 gap-y-6 gap-x-8 items-center md:grid-cols-11">
            <!-- Supplement Facts Table -->
            <div class="order-2 p-1 bg-white rounded-md border-2 border-black md:col-span-12 md:order-1">
                <h2 class="text-xl tracking-wider uppercase xl:text-3xl md:text-2xl font-ww-bold" v-html="tableTitle">
                </h2>
                <div class="pb-1 text-base border-b-8 border-black md:text-xl font-ww-regular" v-html="servingInfo">
                </div>
                <table class="w-full text-sm table-fixed">
                    <colgroup>
                        <col class="w-7/12" />
                        <col class="w-3/12" />
                        <col class="w-2/12" />
                    </colgroup>
                    <thead class="border-b-4 border-black">
                        <tr class="text-base xl:text-2xl md:text-xl !leading-none">
                            <th class="py-1 text-left !font-normal"></th>
                            <th class="py-1 text-right font-ww-bold">
                                Amount Per Serving
                            </th>
                            <th class="py-1 text-right font-ww-bold">% DV*</th>
                        </tr>
                    </thead>
                    <tbody class="space-y-1">
                        <tr v-for="item in nutritionFacts" :key="item.id" 
                            :class="[
                                'text-base xl:text-2xl md:text-xl !leading-none',
                                item.hasBorder ? 'border-b border-black' : '',
                                item.isThickBorder ? 'border-b-4' : ''
                            ]">
                            <td class="py-1" :class="{'pl-4': item.indent}" v-html="item.name"></td>
                            <td class="py-1 text-right whitespace-nowrap font-ww-regular" v-html="item.amount"></td>
                            <td class="py-1 text-right whitespace-nowrap font-ww-bold" v-html="item.dv"></td>
                        </tr>
                    </tbody>
                </table>
                <div class="mt-1 text-base leading-tight border-t-4 border-black xl:text-lg md:text-base">
                    <div class="py-1 border-b border-black font-ww-bold">
                        Ingredients: <span class="font-ww-regular">Alpha GPC (L-alpha glycerylphosphorylcholine), Calcium lactate, Cognatiq® Coffee Fruit Extract, ElectroPrime® (Glucose, Magnesium citrate, HydroPrime® Glycerol, Sodium citrate, Sodium chloride, Potassium chloride), Grassfed Collagen, Organic Arabica Coffee, Organic Chaga Fruiting Body Extract (Inonotus obliquus), Organic Lion's Mane Fruiting Body Extract (Hericium erinaceus)</span>
                    </div>
                     <div class="py-1 text-sm font-ww-regular">
                        * Percent Daily Values based on a 2,000 calorie diet.<br>
                        ↑ Daily Value not established.<br>
                        <span class="font-ww-bold">Other Ingredients:</span> Citric Acid, Glycerol Powder, Natural Orange Flavor, Silicon Dioxide, Steviol Glycosides
                    </div>
                </div>
            </div>
            <!-- Product Image -->
            <div class="hidden order-1 md:col-span-6 md:order-2">
                <div class="">
                   <nuxt-img 
                       :src="productImageSrc" 
                       :sizes="productImageSizes" 
                       :width="productImageWidth" 
                       :height="productImageHeight" 
                       :alt="productImageAlt" 
                       loading="lazy" 
                       class="object-contain w-full h-full" />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
defineProps({
  mainHeading: {
    type: String,
    default: 'Just Scoop, Pour, and Stir to Upgrade Your Day.'
  },
  descriptionText1: {
    type: String,
    default: 'Thousands of research publications support the ingredients that we use in Mindflow'
  },
  tableTitle: {
    type: String,
    default: 'Supplement Facts'
  },
  servingInfo: {
    type: String,
    default: 'Serving Size 1 tbsp (6.48g)<br>Servings Per Container 25'
  },
  nutritionFacts: {
    type: Array,
    default: () => [
      { id: 1, name: 'Calories', amount: '20', dv: '', hasBorder: true, isThickBorder: false },
      { id: 2, name: 'Sodium', amount: '220 mg', dv: '10%', hasBorder: true },
      { id: 3, name: 'Total Carbohydrates', amount: '2 g', dv: '1%*', hasBorder: true },
      { id: 4, name: 'Total Sugars', amount: '2 g', dv: '', hasBorder: true, indent: true },
      { id: 5, name: 'Added Sugars', amount: '2 g', dv: '4%*', hasBorder: true, indent: true },
      { id: 6, name: 'Protein', amount: '3 g', dv: '5%*', hasBorder: true },
      { id: 7, name: 'Vitamin C', amount: '200 mg', dv: '220%', hasBorder: true },
      { id: 8, name: 'Niacin (B3)', amount: '8 mg', dv: '50%', hasBorder: true },
      { id: 9, name: 'Vitamin B6', amount: '0.65 mg', dv: '40%', hasBorder: true },
      { id: 10, name: 'Vitamin B12', amount: '1.2 mcg', dv: '50%', hasBorder: true },
      { id: 11, name: 'Pantothenic Acid', amount: '2.5 mg', dv: '50%', hasBorder: true },
      { id: 12, name: 'Calcium', amount: '40 mg', dv: '4%', hasBorder: true },
      { id: 13, name: 'Potassium', amount: '210 mg', dv: '4%', hasBorder: true },
      { id: 14, name: 'Zinc', amount: '3 mg', dv: '25%', hasBorder: true, isThickBorder: true },
      { id: 15, name: 'Proprietary Blend', amount: '6.48 g', dv: '↑', hasBorder: false },
    ]
  },
  productImageSrc: {
    type: String,
    default: '/assets/images/mindflow-video.jpg'
  },
  productImageSizes: {
    type: String,
    default: 'xs:283px sm:393px md:504px lg:504px xl:504px'
  },
  productImageWidth: {
    type: Number,
    default: 504
  },
  productImageHeight: {
    type: Number,
    default: 475
  },
  productImageAlt: {
    type: String,
    default: 'Glass with Mindflow supplement powder mixed in liquid'
  }
})

defineBuilderComponent({
    friendlyName: 'Product Info With Visual',
    canHaveChildren: false,
    inputs: [
        {
            name: 'mainHeading',
            type: 'richText',
            defaultValue: 'Just Scoop, Pour, and Stir to Upgrade Your Day.'
        },
        {
            name: 'descriptionText1',
            type: 'richText',
            defaultValue: 'Thousands of research publications support the ingredients that we use in Mindflow'
        },
        {
            name: 'tableTitle',
            type: 'text',
            defaultValue: 'Supplement Facts'
        },
        {
            name: 'servingInfo',
            type: 'richText',
            defaultValue: 'Serving Size 1 tbsp (6.48g)<br>Servings Per Container 25'
        },
        {
            name: 'nutritionFacts',
            type: 'list',
            subFields: [
                { name: 'id', type: 'number' },
                { name: 'name', type: 'richText' },
                { name: 'amount', type: 'text', friendlyName: 'Amount (Col 2)' },
                { name: 'dv', type: 'text', friendlyName: '% DV (Col 3)' },
                { name: 'hasBorder', type: 'boolean' },
                { name: 'isThickBorder', type: 'boolean' },
                { name: 'indent', type: 'boolean' }
            ],
            defaultValue: [
                { id: 1, name: 'Calories', amount: '20', dv: '', hasBorder: true, isThickBorder: false },
                { id: 2, name: 'Sodium', amount: '220 mg', dv: '10%', hasBorder: true },
                { id: 3, name: 'Total Carbohydrates', amount: '2 g', dv: '1%*', hasBorder: true },
                { id: 4, name: 'Total Sugars', amount: '2 g', dv: '', hasBorder: true, indent: true },
                { id: 5, name: 'Added Sugars', amount: '2 g', dv: '4%*', hasBorder: true, indent: true },
                { id: 6, name: 'Protein', amount: '3 g', dv: '5%*', hasBorder: true },
                { id: 7, name: 'Vitamin C', amount: '200 mg', dv: '220%', hasBorder: true },
                { id: 8, name: 'Niacin (B3)', amount: '8 mg', dv: '50%', hasBorder: true },
                { id: 9, name: 'Vitamin B6', amount: '0.65 mg', dv: '40%', hasBorder: true },
                { id: 10, name: 'Vitamin B12', amount: '1.2 mcg', dv: '50%', hasBorder: true },
                { id: 11, name: 'Pantothenic Acid', amount: '2.5 mg', dv: '50%', hasBorder: true },
                { id: 12, name: 'Calcium', amount: '40 mg', dv: '4%', hasBorder: true },
                { id: 13, name: 'Potassium', amount: '210 mg', dv: '4%', hasBorder: true },
                { id: 14, name: 'Zinc', amount: '3 mg', dv: '25%', hasBorder: true, isThickBorder: true },
                { id: 15, name: 'Proprietary Blend', amount: '6.48 g', dv: '↑', hasBorder: false },
            ]
        },
        {
            name: 'productImageSrc',
            type: 'file',
            defaultValue: '/assets/images/mindflow-video.jpg'
        },
        {
            name: 'productImageSizes',
            type: 'text',
            defaultValue: 'xs:283px sm:393px md:504px lg:504px xl:504px'
        },
        {
            name: 'productImageWidth',
            type: 'number',
            defaultValue: 504
        },
        {
            name: 'productImageHeight',
            type: 'number',
            defaultValue: 475
        },
        {
            name: 'productImageAlt',
            type: 'text',
            defaultValue: 'Glass with Mindflow supplement powder mixed in liquid'
        }
    ]
})
</script>
